import { useEffect, useState } from "react";
import { getUrl } from "@aws-amplify/storage";
import { OfficeBuildingIcon, UserIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import { BUSINESS_TYPES, BUSINESS_CATEGORIES } from "../../../../Constants/business";
import BusinessStatusBadge from "../BusinessStatusBadge";

// Map the stored sector code ('001'..'011','999') to its label; fall back to
// the raw value so an unknown/legacy code still renders something.
const CATEGORY_LABELS = BUSINESS_CATEGORIES.reduce((acc, { value, label }) => {
  acc[value] = label;
  return acc;
}, {});

const BusinessAvatar = ({ loading, business }) => {
  const { t } = useTranslation();

  const [imageUrl, setImageUrl] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    const getImageUrl = async () => {
      try {
        setIsImageLoading(true);
        const urlResult = await getUrl({
          path: ({ identityId }) =>
            `protected/${identityId}/${business.image.key}`,
          options: {
            validateObjectExistence: true,
            expiresIn: 3600,
            useAccelerateEndpoint: true,
          },
        });

        if (mounted) {
          setImageUrl(urlResult.url.href);
        }
      } catch (error) {
      } finally {
        setIsImageLoading(false);
      }
    };

    if (business?.image?.key) {
      getImageUrl();
    } else {
      setImageUrl(null);
    }

    return () => {
      mounted = false;
    };
  }, [business?.image?.key]);

  return (
    <div className="flex items-center min-w-0">
      {loading || isImageLoading ? (
        <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
      ) : (
        <div className="h-10 w-10 rounded-full bg-gray-100">
          {imageUrl ? (
            <img
              className="h-10 w-10 rounded-full object-cover"
              src={imageUrl}
              alt={business?.name}
              onLoad={() => setIsImageLoading(false)}
              style={{ display: isImageLoading ? "none" : "block" }}
            />
          ) : (
            <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
              {business?.type === "PERSONAL" ? (
                <UserIcon className="h-6 w-6 text-gray-400" />
              ) : (
                <OfficeBuildingIcon className="h-6 w-6 text-gray-400" />
              )}
            </div>
          )}
        </div>
      )}
      <div className="ml-3 min-w-0">
        {loading ? (
          <>
            <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mb-1" />
            <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
          </>
        ) : (
          <>
            {/* Name owns its line and truncates — in the sidebar the column is
                ~180px, so an inline pill used to shove the name into two ragged
                lines and wrap itself as well. The status pill sits under the
                meta line instead: still adjacent, never competing. */}
            <p className="text-sm font-medium text-gray-700 truncate" title={business?.name}>
              {business?.name}
            </p>
            <p className="text-xs text-gray-500">
              <span className="block sm:inline">
                {t(BUSINESS_TYPES[business?.type] || business?.type)}
              </span>{" "}
              {business?.category && (
                <>
                  <span aria-hidden="true" className="hidden sm:mx-1 sm:inline">
                    &middot;
                  </span>{" "}
                  <span className="block sm:inline">
                    {CATEGORY_LABELS[business.category] || business.category}
                  </span>
                </>
              )}
            </p>
            <BusinessStatusBadge business={business} className="mt-1" />
          </>
        )}
      </div>
    </div>
  );
};

export default BusinessAvatar;
